import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, User, X } from 'lucide-react';
import { Button } from './ui/Button';
import { useAuth } from './AuthProvider';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';

export default function HomeHeader({ hideAuthLinks = false, transparent = false }: { hideAuthLinks?: boolean; transparent?: boolean }) {
    const { user, userRoles, signOut } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    // Close on navigation and on Escape; the page must not scroll behind the open menu.
    useEffect(() => { setOpen(false); }, [location.pathname]);
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKey);
        return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
    }, [open]);
    const links = [
        { to: '/about', label: t('nav.about') },
        { to: '/churches', label: 'Church map' },
        { to: '/how-it-works', label: t('nav.howItWorks') },
        { to: '/best-practices', label: t('nav.faqs') },
    ];

    const handleSignOut = async () => {
        try {
            await signOut();
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const handleDashboardClick = () => {
        if (userRoles?.creator) {
            navigate('/creator');
        } else {
            navigate('/dashboard');
        }
    };

    return (
        <header className={transparent ? "absolute top-0 left-0 right-0 z-50 bg-transparent" : "bg-white"}>
            <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-between gap-2" aria-label="Global">
                <div className="flex items-center shrink-0">
                    <Link to="/" className="-m-1.5 p-1.5">
                        <img
                            className="h-7 sm:h-10 w-auto max-w-none"
                            src="/devotionfund-logo.png"
                            alt="Devotion Fund"
                        />
                    </Link>
                </div>

                <div className="hidden md:flex items-center gap-4 lg:gap-8 md:text-sm lg:text-base whitespace-nowrap">
                    <Link
                        to="/about"
                        className="text-foreground/75 hover:text-foreground transition-colors font-medium"
                    >
                        {t('nav.about')}
                    </Link>
                    <Link
                        to="/churches"
                        className="text-foreground/75 hover:text-foreground transition-colors font-medium"
                    >
                        Church map
                    </Link>
                    <Link
                        to="/how-it-works"
                        className="text-foreground/75 hover:text-foreground transition-colors font-medium"
                    >
                        {t('nav.howItWorks')}
                    </Link>
                    <Link
                        to="/best-practices"
                        className="text-foreground/75 hover:text-foreground transition-colors font-medium"
                    >
                        {t('nav.faqs')}
                    </Link>
                </div>

                {/* Phones: the logo and the menu button, nothing else. */}
                <button type="button" onClick={() => setOpen(true)} aria-label="Open menu" aria-expanded={open}
                    className="md:hidden -mr-2 inline-flex h-11 w-11 items-center justify-center rounded-full text-foreground hover:bg-gray-100">
                    <Menu className="h-6 w-6" />
                </button>

                <div className="hidden md:flex items-center gap-2 lg:gap-4">
                    <div className="hidden lg:block"><LanguageSelector /></div>
                    {user ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200">
                                <User className="w-5 h-5 text-gray-600" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={handleDashboardClick}>
                                    {t('nav.dashboard')}
                                </DropdownMenuItem>
                                {userRoles?.church && (
                                    <DropdownMenuItem onClick={() => navigate('/church')}>
                                        Your church
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => navigate('/promotions')}>
                                    Your promotions
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleSignOut}>
                                    {t('actions.logout')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : hideAuthLinks ? null : (
                        <>
                            <Button
                                asChild
                                variant="ghost"
                                className="text-primary hover:bg-primary/10 px-2 sm:px-4"
                            >
                                <Link to="/signin">{t('actions.login')}</Link>
                            </Button>
                            <Button
                                asChild
                                className="bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-full font-semibold px-3 sm:px-4"
                            >
                                <Link to="/get-started">{t('actions.getStarted')}</Link>
                            </Button>
                        </>
                    )}
                </div>
            </nav>

            {open && (
                <div className="fixed inset-0 z-[60] flex flex-col bg-white md:hidden" role="dialog" aria-modal="true" aria-label="Menu">
                    <div className="flex items-center justify-between px-4 py-6">
                        <Link to="/" onClick={() => setOpen(false)}><img className="h-7 w-auto" src="/devotionfund-logo.png" alt="Devotion Fund" /></Link>
                        <button type="button" onClick={() => setOpen(false)} aria-label="Close menu" className="-mr-2 inline-flex h-11 w-11 items-center justify-center rounded-full hover:bg-gray-100">
                            <X className="h-6 w-6" />
                        </button>
                    </div>
                    <nav className="flex-1 overflow-y-auto px-4 pb-8">
                        <ul className="divide-y divide-border border-y border-border">
                            {links.map((l) => (
                                <li key={l.to}><Link to={l.to} onClick={() => setOpen(false)} className="block py-4 text-lg font-semibold text-foreground">{l.label}</Link></li>
                            ))}
                            {user && (
                                <>
                                    <li><button type="button" onClick={() => { setOpen(false); handleDashboardClick(); }} className="block w-full py-4 text-left text-lg font-semibold text-foreground">{t('nav.dashboard')}</button></li>
                                    {userRoles?.church && <li><Link to="/church" onClick={() => setOpen(false)} className="block py-4 text-lg font-semibold text-foreground">Your church</Link></li>}
                                    <li><Link to="/promotions" onClick={() => setOpen(false)} className="block py-4 text-lg font-semibold text-foreground">Your promotions</Link></li>
                                </>
                            )}
                        </ul>
                        <div className="mt-6"><LanguageSelector /></div>
                        <div className="mt-8 grid gap-3">
                            {user ? (
                                <button type="button" onClick={() => { setOpen(false); handleSignOut(); }} className="rounded-full border border-border py-3 text-base font-semibold">{t('actions.logout')}</button>
                            ) : hideAuthLinks ? null : (
                                <>
                                    <Link to="/get-started" onClick={() => setOpen(false)} className="rounded-full bg-secondary py-3 text-center text-base font-semibold text-secondary-foreground">{t('actions.getStarted')}</Link>
                                    <Link to="/signin" onClick={() => setOpen(false)} className="rounded-full border border-border py-3 text-center text-base font-semibold text-primary">{t('actions.login')}</Link>
                                </>
                            )}
                        </div>
                    </nav>
                </div>
            )}
        </header>
    );
} 